import React, { useState, useEffect } from 'react';
import { X, Copy, Download, FileText, File as FileIcon, Link2, Table2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';

// Official Vite-compatible worker setup (pdfjs-dist v4+)
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

interface PreviewFile {
  id: string;
  name: string;
  size_bytes: number;
  mime_type: string | null;
  storage_path: string;
  created_at: string;
}

interface FilePreviewPanelProps {
  file: PreviewFile;
  onClose: () => void;
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/avif', 'image/bmp', 'image/tiff'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/ogg', 'video/x-m4v'];
const AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac', 'audio/x-wav'];
const TEXT_TYPES = ['text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'application/json', 'application/xml', 'text/xml', 'text/markdown'];
const SHEET_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];

const getExt = (name: string): string => name.split('.').pop()?.toLowerCase() || '';

const getPreviewType = (file: PreviewFile): 'image' | 'video' | 'audio' | 'pdf' | 'sheet' | 'text' | 'none' => {
  const mime = file.mime_type || '';
  const ext = getExt(file.name);
  if (IMAGE_TYPES.includes(mime) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'bmp'].includes(ext)) return 'image';
  if (VIDEO_TYPES.includes(mime) || ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) return 'video';
  if (AUDIO_TYPES.includes(mime) || ['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext)) return 'audio';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (SHEET_TYPES.includes(mime) || ['xlsx', 'xls', 'csv'].includes(ext)) return 'sheet';
  if (TEXT_TYPES.includes(mime) || ['txt', 'csv', 'md', 'json', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'log'].includes(ext)) return 'text';
  return 'none';
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({ file, onClose }) => {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textError, setTextError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const previewType = getPreviewType(file);
  const { data: urlData } = supabase.storage.from('file-storage').getPublicUrl(file.storage_path);
  const publicUrl = urlData.publicUrl;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  // Load text content for text files
  useEffect(() => {
    if (previewType !== 'text') return;
    let cancelled = false;
    setTextContent(null);
    setTextError(false);
    fetch(publicUrl)
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then(text => { if (!cancelled) setTextContent(text.slice(0, 50000)); })
      .catch(() => { if (!cancelled) setTextError(true); });
    return () => { cancelled = true; };
  }, [previewType, publicUrl]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      showToast('Link kopiert');
    } catch {
      // Fallback for non-secure contexts
      const textarea = document.createElement('textarea');
      textarea.value = publicUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); showToast('Link kopiert'); } catch { showToast(publicUrl); }
      document.body.removeChild(textarea);
    }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = publicUrl;
    a.download = file.name;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderPreview = () => {
    switch (previewType) {
      case 'image':
        return (
          <img
            src={publicUrl}
            alt={file.name}
            className="w-full h-auto max-h-[500px] object-contain bg-black"
          />
        );
      case 'video':
        return (
          <video
            src={publicUrl}
            controls
            className="w-full max-h-[500px] bg-black rounded-lg"
          />
        );
      case 'audio':
        return (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
              <FileIcon className="w-7 h-7 text-blue-400" />
            </div>
            <audio src={publicUrl} controls className="w-full max-w-xs" />
          </div>
        );
      case 'pdf':
        return <PdfPreview url={publicUrl} />;
      case 'sheet':
        return <SheetPreview url={publicUrl} />;
      case 'text':
        if (textError) {
          return <div className="py-12 text-center text-sm text-gray-500">Preview not available</div>;
        }
        return (
          <pre className="w-full h-[500px] overflow-auto bg-gray-950 text-gray-300 text-xs p-4 rounded-lg whitespace-pre-wrap break-words">
            {textContent ?? 'Loading...'}
          </pre>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <FileText className="w-12 h-12 mb-3" />
            <p className="text-sm">No preview for this file type</p>
          </div>
        );
    }
  };

  return (
    <div className="w-[380px] flex-shrink-0 bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col max-h-[calc(100vh-200px)] sticky top-0 shadow-lg">
      {toast && (
        <div className="absolute top-2 right-2 z-50 bg-gray-900 dark:bg-dark-700 border border-gray-600 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>{file.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {formatBytes(file.size_bytes)} · {formatDate(file.created_at)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0 ml-2"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Preview */}
      <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-950/50">
        {renderPreview()}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
        <button
          onClick={copyLink}
          className="btn-outline flex-1 justify-center !px-3 !py-2 !text-xs"
        >
          <Link2 className="w-3.5 h-3.5" />
          Copy link
        </button>
        <button
          onClick={download}
          className="btn flex-1 justify-center !px-3 !py-2 !text-xs"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </button>
      </div>
    </div>
  );
};

const PdfPreview: React.FC<{ url: string }> = ({ url }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      if (!cancelled) setError(true);
    }, 10000);

    (async () => {
      try {
        const doc = await pdfjsLib.getDocument({ url, isEvalSupported: false }).promise;
        if (cancelled || timedOut) return;
        const page = await doc.getPage(1);
        if (cancelled || timedOut) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Scale to fit panel width (~340px)
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = 340 / baseViewport.width;
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
      } catch (err) {
        console.error('PDF preview error:', err);
        if (!cancelled) setError(true);
      } finally {
        clearTimeout(timeout);
      }
    })();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [url]);

  if (error) {
    return <div className="py-12 text-center text-sm text-gray-500">PDF-Preview not available</div>;
  }

  return (
    <div className="w-full overflow-auto bg-gray-800/50 flex justify-center p-3 max-h-[500px]">
      <canvas ref={canvasRef} className="shadow-lg bg-white" />
    </div>
  );
};

const SheetPreview: React.FC<{ url: string }> = ({ url }) => {
  const [rows, setRows] = useState<any[][] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.arrayBuffer();
      })
      .then(buffer => {
        const wb = XLSX.read(buffer);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 });
        if (!cancelled) setRows(data.slice(0, 100));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return <div className="py-12 text-center text-sm text-gray-500">Tabellen-Preview not available</div>;
  }
  if (!rows) {
    return <div className="py-12 text-center text-sm text-gray-500">Loading table...</div>;
  }

  const maxCols = Math.min(10, Math.max(...rows.map(r => (r || []).length), 1));

  return (
    <div className="w-full max-h-[500px] overflow-auto">
      <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-400 bg-gray-800/50 border-b border-gray-800 sticky top-0">
        <Table2 className="w-3.5 h-3.5 text-green-400" />
        <span>{rows.length} rows</span>
      </div>
      <table className="w-full text-xs border-collapse">
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri === 0 ? 'bg-gray-800/70' : 'hover:bg-gray-800/30'}>
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td key={ci} className="border border-gray-800 px-2 py-1 text-gray-300 truncate max-w-[120px] whitespace-nowrap">
                  {row?.[ci] !== undefined && row[ci] !== null ? String(row[ci]) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
